import {t} from "@lingui/core/macro";
import {StyleSheet, type CSSProperties} from "aphrodite";
import {useCallback, useEffect, useId, useRef, useState} from "react";
import {useParams} from "react-router-dom";

import {IntegerField} from "@khanacademy/event-schemas";
import {Text, useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import {OptionItem} from "@khanacademy/wonder-blocks-dropdown";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {MediaLayout} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import type {SavedStatus} from "@khan/component-save-indicator";
import {useMutation} from "@khan/data-gql";
import {doNotTranslateYet} from "@khan/i18n";
import {Errors, KAError} from "@khan/logging";
import {PermissionsInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";

import PageRoot from "#app-shell-package/page-root.tsx";
import HeaderOnlyPageTemplate from "#app-shell-package/page-templates/header-only-page-template.tsx";
import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import useQAExpandKeyInfo from "#content-library/data-access/use-qa-expand-key-info.ts";
import type {
    ComputingCodeMultiFile,
    QAExpandKeyInfo,
} from "#content-library/types/types.ts";
import FullPageContentContext from "#content-library/ui/full-page-content-context.ts";
import useProfile from "#data-access/use-profile.ts";
import Discussion from "#discussion-package/components/stateful-discussion.tsx";
import type {
    DiscussionContext,
    FeedbackType,
} from "#discussion-package/types.ts";
import TimeAgo from "#react-components-package/timeago.tsx";
import {UPDATE_PROGRAM} from "#scratchpads-package/queries/update-program.ts";
import ScratchpadSpinOffs from "#scratchpads-package/scratchpad-spinoffs.tsx";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import PythonCodingPage from "../python-coding-page.tsx";
import SelectMenu, {useSelectMenuState} from "../select-menu.tsx";
import * as strings from "../strings.tsx";
import {pyIconUrl} from "../thumbnail-icon.ts";

import DeleteButton from "./delete-button.tsx";
import EditTitleButton from "./edit-title-button.tsx";
import Footer from "./footer.tsx";
import Guidelines from "./guidelines.tsx";
import LicenseText from "./license-text.tsx";
import {withStrings} from "./locales/python-user-program-page/load-strings.tsx";
import TabBar from "./tab-bar.tsx";
import TabContents from "./tab-contents.tsx";
import {useUserProgram, type UserProgram} from "./use-user-program.ts";

import caretDownIcon from "@phosphor-icons/core/regular/caret-down.svg";
import caretUpIcon from "@phosphor-icons/core/regular/caret-up.svg";

type TabKey = "program" | "about" | "questions" | "spin-offs";

type UrlParams = {
    slug?: string;
    program_id?: string;
};

function PythonUserProgramPage({
    program,
    profile,
    qaExpandKeyInfo,
}: {
    program: UserProgram;
    profile: ProfileModelType | null | undefined;
    qaExpandKeyInfo: QAExpandKeyInfo | null | undefined;
}): React.ReactElement {
    const [editedCode, setEditedCode] = useState<ComputingCodeMultiFile | null>(
        null,
    );
    const [title, setTitle] = useState<string | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [selectedTab, setSelectedTab] = useState<TabKey>(
        qaExpandKeyInfo == null ? "program" : "questions",
    );

    const [isContentFullPage, setIsContentFullPage] = useState(false);

    const programTabId = useId();
    const programPanelId = useId();
    const aboutTabId = useId();
    const aboutPanelId = useId();
    const questionsTabId = useId();
    const questionsPanelId = useId();
    const spinOffsTabId = useId();
    const spinOffsPanelId = useId();

    // We only want to send the ComputingUserProgramCodeEdited at most once each
    // time the program is loaded, so we use a ref to track whether we've sent
    // it.
    const hasSentCodeEditedEventRef = useRef(false);

    const mobileNavigationMenuTriggerRef = useRef<HTMLButtonElement | null>(
        null,
    );
    const mobileNavigationMenu = useSelectMenuState(
        mobileNavigationMenuTriggerRef,
    );

    useEffect(() => {
        if (!hasUnsavedChanges) {
            return;
        }

        const stopUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = true;
        };

        window.addEventListener("beforeunload", stopUnload);
        return () => window.removeEventListener("beforeunload", stopUnload);
    }, [hasUnsavedChanges]);

    const [updateProgramMutation, updateResult] = useMutation(UPDATE_PROGRAM);

    const initialCodeJSON = program.latestRevision.code;

    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    const programIdInt = parseInt(program.id);
    useOnMountEffect(() => {
        publishAnalyticsEvent({
            eventType: "ComputingUserProgramLoaded",
            eventSchemaVersion: 3,
            programId: new IntegerField(programIdInt),
            userAuthoredContentType: "PYTHON",
        });
    });

    if (program.userAuthoredContentType !== "PYTHON") {
        throw new KAError(
            "cannot open non-Python program in Python coding UI",
            Errors.Internal,
        );
    }

    const savedStatus: SavedStatus = (() => {
        if (updateResult == null) {
            return "unsaved";
        }
        if (updateResult.status === "loading") {
            return "saving";
        }
        if (
            updateResult.status !== "success" ||
            updateResult.data.updateProgram == null ||
            updateResult.data.updateProgram.error != null
        ) {
            return "error";
        }
        return hasUnsavedChanges ? "unsaved" : "saved";
    })();

    const titleWithFallback = title ?? program.title;
    const updateProgram = useCallback(
        (newTitle?: string) => {
            setHasUnsavedChanges(false);
            updateProgramMutation({
                programId: program.id,
                title: newTitle ?? titleWithFallback,
                revision: {
                    code:
                        editedCode == null
                            ? initialCodeJSON
                            : JSON.stringify(editedCode),
                    configVersion: 0,
                    folds: [],
                    imageUrl: pyIconUrl,
                },
            })
                .then((result) => {
                    if (
                        result.status !== "success" ||
                        result.data.updateProgram == null ||
                        result.data.updateProgram.error != null
                    ) {
                        setHasUnsavedChanges(true);
                    }
                })
                .catch(() => setHasUnsavedChanges(true));
        },
        [
            editedCode,
            initialCodeJSON,
            program.id,
            titleWithFallback,
            updateProgramMutation,
        ],
    );

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const isControlOrCommandHeld = event.metaKey || event.ctrlKey;
            if (isControlOrCommandHeld && event.key === "s") {
                event.preventDefault();
                if (hasUnsavedChanges) {
                    updateProgram();
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [hasUnsavedChanges, updateProgram]);

    const canEdit = PermissionsInfo.hasAnyPermissions || program.isOwner;
    const saveButtonProps = canEdit
        ? {
              disabled: !hasUnsavedChanges,
              spinner: updateResult?.status === "loading",
              onClick: () => updateProgram(),
          }
        : undefined;

    const editTitleButtonProps = {
        currentTitle: titleWithFallback,
        onSave: (newTitle) => {
            updateProgram(newTitle);
            setTitle(newTitle);
        },
    };

    const mobileTitles: Record<TabKey, string> = {
        program: titleWithFallback,
        about: doNotTranslateYet`About`,
        questions: doNotTranslateYet`Questions`,
        "spin-offs": doNotTranslateYet`Spin-offs`,
    };

    const discussionContext: DiscussionContext = {
        authorKaid: program.author.id,
        restrictPosting: program.restrictPosting,
        showProjectFeedback: !(program.byChild && program.isOwner),
    };
    if (qaExpandKeyInfo?.unencryptedKey != null) {
        discussionContext.qaExpandKey = qaExpandKeyInfo.unencryptedKey;
    }
    const feedbackType = asFeedbackType(qaExpandKeyInfo?.feedbackType);
    if (feedbackType != null) {
        discussionContext.expandFeedbackType = feedbackType;
    }

    const handleCodeChange = (code: ComputingCodeMultiFile) => {
        setEditedCode(code);
        setHasUnsavedChanges(true);

        if (!hasSentCodeEditedEventRef.current) {
            hasSentCodeEditedEventRef.current = true;
            publishAnalyticsEvent({
                eventType: "ComputingUserProgramCodeEdited",
                eventSchemaVersion: 3,
                programId: new IntegerField(programIdInt),
                userAuthoredContentType: "PYTHON",
            });
        }
    };

    function getNickname(): React.ReactNode {
        const author = program.author;
        const nickname = author?.nickname;
        const dataUserKaid = author?.id;

        if (!author || !nickname) {
            return <Text data-user-kaid={dataUserKaid}>{t`New visitor`}</Text>;
        }

        const profileRoot = author.profileRoot;
        const accessLevel = author.profile.accessLevel;
        const currentUserIsModerator = profile && profile.get("isModerator");

        if (
            profileRoot &&
            (accessLevel === "PUBLIC" || currentUserIsModerator)
        ) {
            return (
                <Link href={profileRoot} data-user-kaid={dataUserKaid}>
                    {nickname}
                </Link>
            );
        }

        return <Text data-user-kaid={dataUserKaid}>{nickname}</Text>;
    }

    const embeddedBreakpoints =
        new URLSearchParams(location.search).get("embeddedBreakpoints") ===
        "true";
    const styles = embeddedBreakpoints ? embeddedStyles : standaloneStyles;

    const tabs: Array<{
        key: TabKey;
        title: string;
        tabId: string;
        panelId: string;
        content: React.ReactNode;
    }> = [
        {
            key: "program",
            title: doNotTranslateYet`Program`,
            tabId: programTabId,
            panelId: programPanelId,
            content: (
                <PythonCodingPage
                    codeFormat={program.codeFormat}
                    savedStatus={savedStatus}
                    saveButton={saveButtonProps}
                    onCodeChange={handleCodeChange}
                    loggingMetadata={{}}
                    initialCodeJSON={initialCodeJSON}
                    canManageFiles={true}
                    renderFooter={({runButton, code}) => (
                        <Footer
                            program={program}
                            profile={profile}
                            runButton={runButton}
                            code={code}
                            saveButtonProps={saveButtonProps}
                            useEmbeddedStyles={embeddedBreakpoints}
                        />
                    )}
                    useEmbeddedStyles={embeddedBreakpoints}
                />
            ),
        },
        {
            key: "about",
            title: doNotTranslateYet`About`,
            tabId: aboutTabId,
            panelId: aboutPanelId,
            content: (
                <View style={styles.simpleTabContent}>
                    <View style={styles.aboutTabHeader}>
                        <View style={styles.metadata}>
                            {program.originProgram != null && (
                                <LabelMedium>
                                    {strings.basedOn(
                                        <Link href={program.originProgram.url}>
                                            {program.originProgram.title}
                                        </Link>,
                                    )}
                                </LabelMedium>
                            )}
                            <LabelMedium>
                                {strings.createdByAndLastUpdated(
                                    getNickname(),
                                    <TimeAgo
                                        time={program.latestRevision.created}
                                    />,
                                )}
                            </LabelMedium>
                        </View>
                        {canEdit && (
                            <>
                                <EditTitleButton
                                    kind="secondary"
                                    style={styles.aboutTabEditTitleButton}
                                    {...editTitleButtonProps}
                                />
                                <DeleteButton
                                    programId={program.id}
                                    parentCurationNodeUrl={
                                        program.parentCurationNode.relativeUrl
                                    }
                                />
                            </>
                        )}
                    </View>
                    <LicenseText />
                    <View style={styles.aboutTabSeparator} />
                    <Guidelines />
                </View>
            ),
        },
        {
            key: "questions",
            title: doNotTranslateYet`Questions`,
            tabId: questionsTabId,
            panelId: questionsPanelId,
            content: (
                <View
                    style={[
                        styles.simpleTabContent,
                        styles.questionsTabContent,
                    ]}
                >
                    <Discussion
                        contentKind="scratchpad"
                        contentId={program.id}
                        topicSlug={program.parentCurationNode.slug}
                        onNextPage={() => {}}
                        discussionContext={discussionContext}
                    />
                </View>
            ),
        },
        {
            key: "spin-offs",
            title: doNotTranslateYet`Spin-offs`,
            tabId: spinOffsTabId,
            panelId: spinOffsPanelId,
            content: (
                <View style={styles.simpleTabContent}>
                    <ScratchpadSpinOffs scratchpadId={program.id} />
                </View>
            ),
        },
    ];

    const header = (
        <>
            <View style={styles.header}>
                <Text tag="h1" style={styles.desktopTitle}>
                    {titleWithFallback}
                </Text>
                <Text tag="h1" style={styles.mobileTitle}>
                    {mobileTitles[selectedTab]}
                </Text>
                {canEdit && (
                    <EditTitleButton
                        kind="tertiary"
                        style={styles.headerEditTitleButton}
                        {...editTitleButtonProps}
                    />
                )}
                <IconButton
                    icon={
                        mobileNavigationMenu.isOpen
                            ? caretUpIcon
                            : caretDownIcon
                    }
                    kind="tertiary"
                    onClick={mobileNavigationMenu.onToggle}
                    role="listbox"
                    aria-label={
                        mobileNavigationMenu.isOpen
                            ? doNotTranslateYet`Close program navigation menu`
                            : doNotTranslateYet`Open program navigation menu`
                    }
                    style={styles.navigationMenuButton}
                    ref={mobileNavigationMenuTriggerRef}
                />
            </View>
            <View style={styles.tabBar}>
                <TabBar
                    tabs={tabs.map(({key, title, tabId, panelId}) => ({
                        key,
                        title,
                        tabId,
                        panelId,
                    }))}
                    selectedTab={selectedTab}
                    onSelectTab={setSelectedTab}
                />
            </View>
        </>
    );

    const mobileNavMenu = (
        <View style={styles.mobileNavigationMenu}>
            <SelectMenu
                buttons={tabs.map(({key, title}) => ({
                    key,
                    render: (ref) => (
                        <OptionItem
                            ref={ref}
                            variant="check"
                            role="menuitem"
                            label={title}
                            value={key}
                            selected={key === selectedTab}
                            onClick={() => {
                                setSelectedTab(key);
                                mobileNavigationMenu.onClose(null);
                            }}
                        />
                    ),
                }))}
                initialFocusedButtonIndex={tabs.findIndex(
                    ({key}) => key === selectedTab,
                )}
                onClose={mobileNavigationMenu.onClose}
            />
        </View>
    );

    return (
        <FullPageContentContext.Provider
            value={{isContentFullPage, setIsContentFullPage}}
        >
            <MediaLayout>
                {({mediaSize}) => {
                    // Use a MediaLayout here, even though it's deprecated,
                    // to match the behavior in the ContentPageWrapper.
                    const isSmallScreen = mediaSize === "small";
                    if (isSmallScreen && isContentFullPage) {
                        // Disable full-page content on small screens
                        setIsContentFullPage(false);
                    }

                    return (
                        <PageRoot
                            template={HeaderOnlyPageTemplate}
                            settings={{header: !isContentFullPage}}
                            resetSettings={false}
                        >
                            <View style={styles.wrapper}>
                                {!isContentFullPage && header}
                                <View style={styles.mainContent}>
                                    <TabContents
                                        tabs={tabs.map(
                                            ({
                                                key,
                                                tabId,
                                                panelId,
                                                content,
                                            }) => ({
                                                key,
                                                tabId,
                                                panelId,
                                                content,
                                            }),
                                        )}
                                        selectedTab={selectedTab}
                                    />
                                    {mobileNavigationMenu.isOpen &&
                                        mobileNavMenu}
                                </View>
                            </View>
                        </PageRoot>
                    );
                }}
            </MediaLayout>
        </FullPageContentContext.Provider>
    );
}

function PythonUserProgramPageDataWrapper(): React.ReactElement {
    const {slug, program_id: programId} = useParams<UrlParams>();
    if (slug == null || programId == null) {
        throw new KAError("missing URL params", Errors.Internal);
    }

    const programResult = useUserProgram(programId);
    const profileResult = useProfile();
    const [qaExpandKeyInfoResult] = useQAExpandKeyInfo();
    if (
        programResult.status === "loading" ||
        profileResult.status === "loading" ||
        qaExpandKeyInfoResult.status === "loading"
    ) {
        return <RouteLoadSpinner />;
    }
    if (programResult.status !== "success") {
        throw new KAError("failed to load user program", Errors.Internal);
    }
    if (profileResult.status !== "success") {
        throw new KAError("failed to load profile", Errors.Internal);
    }
    if (
        qaExpandKeyInfoResult.status !== "success" &&
        qaExpandKeyInfoResult.status !== "skipped"
    ) {
        throw new KAError("failed to load QA expand key info", Errors.Internal);
    }

    return (
        <PythonUserProgramPage
            program={programResult.data}
            profile={profileResult.profile}
            qaExpandKeyInfo={
                qaExpandKeyInfoResult.status === "success"
                    ? qaExpandKeyInfoResult.data
                    : null
            }
        />
    );
}

export default withStrings(PythonUserProgramPageDataWrapper);

function asFeedbackType(
    feedbackType: string | null | undefined,
): FeedbackType | null {
    if (
        feedbackType === "questions" ||
        feedbackType === "comments" ||
        feedbackType === "answers" ||
        feedbackType === "replies" ||
        feedbackType === "clarifications" ||
        feedbackType === "projecthelp" ||
        feedbackType === "projectquestions" ||
        feedbackType === "projectanswers"
    ) {
        return feedbackType;
    }
    return null;
}

// creates padding to make a container with a maximum internal width, with
// minimum horizontal padding of 16px in case the screen is small.
function getMaxWidthPadding(maxWidth: number): CSSProperties {
    const padding = `max(calc(50% - ${maxWidth / 2}px), ${
        spacing.medium_16
    }px)`;

    return {
        paddingLeft: padding,
        paddingRight: padding,
    };
}
/* eslint-disable react-native/no-unused-styles */
const getStyles = (useEmbeddedStyles: boolean) =>
    StyleSheet.create({
        wrapper: {
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
        },
        header: {
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: spacing.small_12,
            marginTop: 20,
            marginBottom: spacing.xxSmall_6,
            flexShrink: 0,
            [useEmbeddedStyles
                ? mediaQueries.xsOrSmaller
                : mediaQueries.smOrSmaller]: {
                justifyContent: "space-between",
                paddingLeft: spacing.medium_16,
                paddingRight: spacing.medium_16,
                marginBottom: 20,
            },
        },
        headerEditTitleButton: {
            height: "auto",
            [useEmbeddedStyles
                ? mediaQueries.xsOrSmaller
                : mediaQueries.smOrSmaller]: {
                display: "none",
            },
        },
        desktopTitle: {
            fontSize: 24,
            lineHeight: "28px",
            fontWeight: "bold",
            [useEmbeddedStyles
                ? mediaQueries.xsOrSmaller
                : mediaQueries.smOrSmaller]: {
                display: "none",
            },
        },
        mobileTitle: {
            fontSize: 16,
            lineHeight: "20px",
            fontWeight: "bold",
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                display: "none",
            },
        },
        navigationMenuButton: {
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                display: "none",
            },
        },
        tabBar: {
            flexShrink: 0,
            [useEmbeddedStyles
                ? mediaQueries.xsOrSmaller
                : mediaQueries.smOrSmaller]: {
                display: "none",
            },
        },
        mainContent: {
            position: "relative",
            flexGrow: 1,
        },
        mobileNavigationMenu: {
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
        },
        simpleTabContent: {
            borderTop: `1px solid ${color.offBlack16}`,
            paddingTop: spacing.xxLarge_48,
            ...getMaxWidthPadding(650),
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: spacing.medium_16,
        },
        aboutTabHeader: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: spacing.small_12,
            flexShrink: 0,
            [useEmbeddedStyles
                ? mediaQueries.xsOrSmaller
                : mediaQueries.smOrSmaller]: {
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
            },
        },
        metadata: {
            display: "flex",
            flexDirection: "column",
            gap: spacing.xxxSmall_4,
            flexGrow: 1,
            flexShrink: 0,
            [useEmbeddedStyles
                ? mediaQueries.xsOrSmaller
                : mediaQueries.smOrSmaller]: {
                gridColumn: "span 2",
            },
        },
        aboutTabEditTitleButton: {
            flexShrink: 0,
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                display: "none",
            },
        },
        aboutTabSeparator: {
            height: 1,
            marginTop: spacing.small_12,
            marginBottom: spacing.small_12,
            backgroundColor: color.offBlack16,
            flexShrink: 0,
        },
        questionsTabContent: {
            [useEmbeddedStyles
                ? mediaQueries.xsOrSmaller
                : mediaQueries.smOrSmaller]: {
                padding: 0,
            },
        },
    });

const embeddedStyles = getStyles(true);
const standaloneStyles = getStyles(false);
